// UsageWarningBanner.tsx — the ≥80%-of-message-cap amber warning (M6 Task 3, spec D1).
// Shared between the Billing screen (always visible while over 80%, never dismissible — it's
// the one place a member goes specifically to fix this) and BotOverview's condensed one-liner
// (dismissible per session via sessionStorage — a nudge, not a screen the visitor came for).
// Reads the same `BillingSummaryDto` both screens already fetch via `useBillingSummary`
// (React Query dedupes the two callers under one cache entry), so there is no new endpoint.
import { useState } from 'react';
import type { BillingSummaryDto } from '@repo/shared-types';
import { useT } from '../locales/context';

const DISMISS_KEY_PREFIX = 'embent:usage-warning-dismissed:';
const WARN_THRESHOLD_PCT = 80;

/** Rounded percentage of the plan's monthly message cap already used this UTC calendar
 * month. A 0 cap (shouldn't happen — every `PLAN_LIMITS` row has `maxMessages > 0`) is
 * treated as fully consumed rather than producing `Infinity`. */
function messageUsagePct(summary: BillingSummaryDto): number {
  // Internal workspaces have no caps at all (cap === null on the wire) — never warn.
  // The old `cap <= 0` guard fired for them because `null <= 0` is true in JS.
  if (summary.internal) return 0;
  const { messagesThisMonth } = summary.usage;
  const cap = summary.plan.caps.msgPerMonth;
  if (cap === null) return 0;
  if (cap <= 0) return 100;
  return Math.round((messagesThisMonth / cap) * 100);
}

interface UsageWarningBannerProps {
  summary: BillingSummaryDto;
  /** When set, the banner renders a dismiss control and remembers the dismissal in
   * `sessionStorage` under this key for the rest of the browser tab's session (BotOverview,
   * keyed per bot). Omitted on the Billing screen — that banner is never dismissible. */
  dismissKey?: string;
}

export function UsageWarningBanner({ summary, dismissKey }: UsageWarningBannerProps) {
  const t = useT();
  const storageKey = dismissKey ? `${DISMISS_KEY_PREFIX}${dismissKey}` : null;
  const [dismissed, setDismissed] = useState(
    () => storageKey !== null && sessionStorage.getItem(storageKey) === '1',
  );

  const pct = messageUsagePct(summary);
  if (pct < WARN_THRESHOLD_PCT || dismissed) return null;

  return (
    <div
      role="status"
      className="flex items-center justify-between gap-3 rounded-lg border px-4 py-2.5 text-[13px]"
      style={{
        borderColor: 'var(--st-paused)',
        background: 'color-mix(in oklch, var(--st-paused) 12%, transparent)',
      }}
    >
      <span>{t.shell.usageWarning({ pct })}</span>
      {storageKey && (
        <button
          type="button"
          aria-label={t.shell.dismiss}
          onClick={() => {
            sessionStorage.setItem(storageKey, '1');
            setDismissed(true);
          }}
          className="shrink-0 text-muted-foreground hover:text-foreground"
        >
          ✕
        </button>
      )}
    </div>
  );
}
